import { useEffect, useState } from "react";
import { Check, FolderOpen, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function CategoryManagerModal({ open, onClose, categories = [], onChanged }) {
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setNewName("");
      setEditing(null);
    }
  }, [open]);

  const run = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const name = newName.trim();
    if (!name) return;
    run(async () => {
      await api.categories.create(name);
      setNewName("");
    }, "Category added");
  };

  const saveRename = () => {
    const name = editName.trim();
    if (!name) return;
    run(async () => {
      await api.categories.rename(editing, name);
      setEditing(null);
    }, "Category renamed");
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4" /> Categories
          </DialogTitle>
          <DialogDescription>
            Drag the headings on the main screen to change their order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="New category name…"
              className="flex-1"
            />
            <Button onClick={add} disabled={busy || !newName.trim()} size="icon">
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {categories.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">No categories yet.</p>
            )}

            {categories.map((category) =>
              editing === category.id ? (
                <div key={category.id} className="flex items-center gap-2">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename();
                      if (e.key === "Escape") setEditing(null);
                    }}
                    className="flex-1 h-9"
                    autoFocus
                  />
                  <button onClick={saveRename} className="text-green-600 hover:text-green-700" aria-label="Save name">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600" aria-label="Cancel">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div key={category.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 group">
                  <span className="text-sm font-medium truncate">{category.name}</span>
                  <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        setEditing(category.id);
                        setEditName(category.name);
                      }}
                      className="text-gray-400 hover:text-slate-700"
                      aria-label={`Rename ${category.name}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() =>
                        run(() => api.categories.remove(category.id), `"${category.name}" removed`)
                      }
                      className="text-gray-400 hover:text-red-500"
                      aria-label={`Delete ${category.name}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ),
            )}
          </div>

          <p className="text-xs text-gray-500">
            Deleting a category keeps its apps — they move to General.
          </p>
        </div>

        <Button variant="outline" onClick={onClose} className="w-full">
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}
